import * as React from 'react';
import * as api from '../services/api-client';

/**
 * Subdomain state management for the Admin Console.
 * Manages subdomains within a specific domain context.
 * System_Admin + Domain_Admin (scoped to assigned domains).
 * Implements optimistic updates with revert on failure.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Subdomain {
  readonly subdomainId: string;
  readonly subdomainLabel: string;
  readonly fqdn: string;
  readonly domainId: string;
  readonly createdAt: string;
  readonly createdBy: string;
}

export interface CreateSubdomainInput {
  readonly domainId: string;
  readonly subdomain: string;
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface SubdomainState {
  readonly subdomains: readonly Subdomain[];
  readonly isLoading: boolean;
  readonly error: string | null;
}

export const initialSubdomainState: SubdomainState = {
  subdomains: [],
  isLoading: false,
  error: null,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type SubdomainAction =
  | { readonly type: 'SUBDOMAINS_LOADING' }
  | { readonly type: 'SUBDOMAINS_LOADED'; readonly payload: readonly Subdomain[] }
  | { readonly type: 'SUBDOMAINS_ERROR'; readonly payload: string }
  | { readonly type: 'SUBDOMAIN_CREATE_OPTIMISTIC'; readonly payload: Subdomain }
  | { readonly type: 'SUBDOMAIN_CREATE_CONFIRMED' }
  | { readonly type: 'SUBDOMAIN_CREATE_REVERT'; readonly payload: { readonly previousState: readonly Subdomain[] } };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function subdomainReducer(state: SubdomainState, action: SubdomainAction): SubdomainState {
  switch (action.type) {
    case 'SUBDOMAINS_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'SUBDOMAINS_LOADED':
      return { subdomains: action.payload, isLoading: false, error: null };
    case 'SUBDOMAINS_ERROR':
      return { ...state, isLoading: false, error: action.payload };
    case 'SUBDOMAIN_CREATE_OPTIMISTIC':
      return { ...state, subdomains: [...state.subdomains, action.payload], error: null };
    case 'SUBDOMAIN_CREATE_CONFIRMED':
      return state;
    case 'SUBDOMAIN_CREATE_REVERT':
      return { ...state, subdomains: action.payload.previousState, error: 'Failed to create subdomain. Reverted.' };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface SubdomainContextValue {
  readonly state: SubdomainState;
  readonly loadSubdomains: (domainId: string) => Promise<void>;
  readonly createSubdomain: (input: CreateSubdomainInput) => Promise<void>;
}

const SubdomainContext = React.createContext<SubdomainContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface SubdomainProviderProps {
  readonly children: React.ReactNode;
}

/**
 * SubdomainProvider manages subdomain list state with optimistic updates.
 */
export function SubdomainProvider({ children }: SubdomainProviderProps): React.JSX.Element {
  const [state, dispatch] = React.useReducer(subdomainReducer, initialSubdomainState);
  const stateRef = React.useRef(state.subdomains);
  stateRef.current = state.subdomains;

  const loadSubdomains = React.useCallback(async (domainId: string): Promise<void> => {
    dispatch({ type: 'SUBDOMAINS_LOADING' });
    try {
      const data = await api.get<{ subdomains: readonly Subdomain[] }>(`/admin/domains/${domainId}/subdomains`);
      dispatch({ type: 'SUBDOMAINS_LOADED', payload: data.subdomains });
    } catch (err) {
      dispatch({
        type: 'SUBDOMAINS_ERROR',
        payload: err instanceof Error ? err.message : 'Failed to load subdomains',
      });
    }
  }, []);

  const createSubdomain = React.useCallback(async (input: CreateSubdomainInput): Promise<void> => {
    const previousState = stateRef.current;
    const optimistic: Subdomain = {
      subdomainId: crypto.randomUUID(),
      subdomainLabel: input.subdomain,
      fqdn: `${input.subdomain}.pending`,
      domainId: input.domainId,
      createdAt: new Date().toISOString(),
      createdBy: 'current-user',
    };

    dispatch({ type: 'SUBDOMAIN_CREATE_OPTIMISTIC', payload: optimistic });
    try {
      await api.post(`/admin/domains/${input.domainId}/subdomains`, { subdomain: input.subdomain });
      dispatch({ type: 'SUBDOMAIN_CREATE_CONFIRMED' });
      // Reload to get server-assigned IDs
      await loadSubdomains(input.domainId);
    } catch {
      dispatch({ type: 'SUBDOMAIN_CREATE_REVERT', payload: { previousState } });
    }
  }, [loadSubdomains]);

  const contextValue = React.useMemo(
    (): SubdomainContextValue => ({
      state,
      loadSubdomains,
      createSubdomain,
    }),
    [state, loadSubdomains, createSubdomain],
  );

  return React.createElement(SubdomainContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access subdomain state and actions.
 * Must be used within a SubdomainProvider.
 */
export function useSubdomains(): SubdomainContextValue {
  const context = React.useContext(SubdomainContext);
  if (!context) {
    throw new Error('useSubdomains must be used within a SubdomainProvider');
  }
  return context;
}
